import type { User as Auth0User } from '@auth0/auth0-react'
import type { User, UserRole } from '@/types/user'
import { AUTH0_CLAIMS } from './auth0-config'

/**
 * Studio membership as stored in Auth0 app_metadata
 */
export interface Auth0StudioMembership {
  studioId: string
  role: UserRole
  isPrimary: boolean
}

/**
 * Extended Auth0 user with custom claims
 */
export interface Auth0UserWithClaims extends Auth0User {
  [AUTH0_CLAIMS.ROLES]?: UserRole[]
  [AUTH0_CLAIMS.STUDIOS]?: Auth0StudioMembership[]
  [AUTH0_CLAIMS.PRIMARY_STUDIO]?: string
}

/**
 * Map Auth0 user to AerialFlow User type
 *
 * @param auth0User - User object from Auth0 SDK
 * @returns Mapped User object compatible with AerialFlow types
 */
export function mapAuth0UserToUser(auth0User: Auth0UserWithClaims): User {
  const roles = auth0User[AUTH0_CLAIMS.ROLES] || ['aerialist']

  return {
    id: auth0User.sub || '',
    email: auth0User.email || '',
    name: auth0User.name || auth0User.nickname || auth0User.email || 'User',
    avatar: auth0User.picture,
    roles: roles as UserRole[],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

/**
 * Extract primary studio ID from Auth0 user claims
 *
 * @param auth0User - User object from Auth0 SDK with custom claims
 * @returns Primary studio ID or null if not set
 */
export function getPrimaryStudioId(auth0User: Auth0UserWithClaims): string | null {
  return auth0User[AUTH0_CLAIMS.PRIMARY_STUDIO] || null
}

/**
 * Extract all studio memberships from Auth0 user claims
 *
 * @param auth0User - User object from Auth0 SDK with custom claims
 * @returns Array of studio memberships
 */
export function getStudioMemberships(
  auth0User: Auth0UserWithClaims
): Auth0StudioMembership[] {
  return auth0User[AUTH0_CLAIMS.STUDIOS] || []
}

/**
 * Check if user has a specific role
 *
 * @param auth0User - User object from Auth0 SDK with custom claims
 * @param role - Role to check for
 * @returns True if user has the role
 */
export function hasRole(auth0User: Auth0UserWithClaims, role: UserRole): boolean {
  const roles = auth0User[AUTH0_CLAIMS.ROLES] || []
  return roles.includes(role)
}
